import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFetch } from "../../../../hooks/api";
import { VStack, Center } from "../../../../../styled-system/jsx";
import { Button } from "../../../../components/core/basic/Button";
import Loading from "../../../../components/core/blocks/Loading";
import { useDates } from "../../../../dates/DatesContext";
import { DayPartingForm } from "./DayPartingForm";
import { useBulkUpdateDayparting } from "../../api/useBulkUpdateDayparting";
import type { BidModifiers, DaypartingData } from "../types";

interface AdPlanData {
  ad_control_fields?: {
    day_parting_method?: 0 | 1;
    day_parting_multipliers?: BidModifiers;
  };
}

interface DayPartingChartProps {
  advertisement: {
    id: number;
    state: string;
    ad_source?: string;
    managed?: string;
  };
  categoryId?: number;
}

export function DayPartingChart({ advertisement, categoryId }: DayPartingChartProps) {
  const { t } = useTranslation(["common"]);
  const { start, end } = useDates();
  const { data: adPlanData, isLoading } = useFetch<AdPlanData>(
    ["media-plan", advertisement.id],
    "/api/gvads/advproduct/",
    { advertisement: advertisement.id },
    { select: (d) => d.data, enabled: !!advertisement.id },
  );

  const initialValue = useMemo((): DaypartingData => {
    const controlFields = adPlanData?.ad_control_fields;
    return {
      day_parting_method: controlFields?.day_parting_method ?? 0,
      day_parting_multipliers: controlFields?.day_parting_multipliers ?? {},
    };
  }, [adPlanData]);

  const [currentValue, setCurrentValue] = useState<DaypartingData | null>(null);

  const isDirty = useMemo(() => {
    if (!currentValue) return false;
    if (currentValue.day_parting_method !== initialValue.day_parting_method) return true;
    return (
      JSON.stringify(currentValue.day_parting_multipliers) !== JSON.stringify(initialValue.day_parting_multipliers)
    );
  }, [currentValue, initialValue]);

  const mutation = useBulkUpdateDayparting();

  const handleSave = () => {
    if (!currentValue) return;
    mutation.mutate({
      advertisement_ids: [advertisement.id],
      day_parting_method: currentValue.day_parting_method,
      day_parting_multipliers: currentValue.day_parting_multipliers,
    });
  };

  let canUpdate = true;
  if (advertisement.ad_source === "Imported") {
    canUpdate = advertisement.managed === "Yes";
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <VStack gap="medium" alignItems="stretch">
      <DayPartingForm
        advertisements={[advertisement.id]}
        categoryId={categoryId}
        initialValue={initialValue}
        onChange={setCurrentValue}
        canUpdate={canUpdate}
        startDate={start.toDate()}
        endDate={end.toDate()}
      />

      {canUpdate && (
        <Center>
          <Button onPress={handleSave} isDisabled={!isDirty} isPending={mutation.isPending}>
            {t(($) => $.common.actions.Save)}
          </Button>
        </Center>
      )}
    </VStack>
  );
}
